/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState, ReactNode, useContext } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React TS examples components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavList from "examples/Sidenav/SidenavList";
import SidenavItem from "examples/Sidenav/SidenavItem";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";
import { Route } from "routes";
import BitumioContext, { useBitumioContext } from "providers/BitumioContext";
import { Person, getFullName } from "utils/people/getFullName";
import SidenavLink from "./SidenavLink";
import getImageAssetURL from "utils/images/getImageAssetURL";
import { User } from "generated/graphql";
import { checkPermissions } from "helpers/functions";
import TourHelpButton from "components/PageTour/TourHelpButton";

// Declaring props types for Sidenav
interface Props {
  color?: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark";
  brand?: string;
  routes: Route[];
  onHelpClick?: () => void;
  [key: string]: any;
}

const tourClass = (key?: string) => (key ? `tour-nav-${key.replace(/\./g, "-")}` : undefined);

function Sidenav({
  mobileNavbar,
  color,
  brand,
  routes,
  brandLogo,
  onHelpClick,
  ...rest
}: Props): JSX.Element {
  const [openCollapse, setOpenCollapse] = useState<boolean | string>(false);
  const [openNestedCollapse, setOpenNestedCollapse] = useState<boolean | string>(false);
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];
  const items = pathname.split("/").slice(1);
  const itemParentName = items[1];
  const itemName = items[items.length - 1];

  const { signedInUser } = useContext(BitumioContext);

  let textColor:
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "dark"
    | "white"
    | "inherit"
    | "text"
    | "light" = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    setOpenCollapse(collapseName);
    setOpenNestedCollapse(itemParentName);
  }, []);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /**
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the nested collapse items from the routes.js
  const renderNestedCollapse = (collapse: any, user?: User) => {
    const template = collapse
      ?.filter((x) => (x.permission && user ? checkPermissions(user, x.permission) : true))
      .map(({ name, route, key, href }: any, i: number) =>
        href ? (
          <Link
            key={key ?? i}
            href={href}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavItem name={name} nested className={tourClass(key)} />
          </Link>
        ) : (
          <NavLink to={route} key={key ?? i} style={{ textDecoration: "none" }}>
            <SidenavItem
              data-testid="render-nested-collapse"
              name={name}
              active={route === pathname || pathname?.includes(route)}
              nested
              className={tourClass(key)}
            />
          </NavLink>
        )
      );

    return template;
  };
  // Render the all the collpases from the routes.js
  const renderCollapse = (collapses: any, user?: User) =>
    collapses
      ?.filter((x) => (x.permission && user ? checkPermissions(user, x.permission) : true))
      .map(({ name, collapse, route, href, key, type }: any, i: number) => {
        if (type === "internal") {
          return null;
        }

        let returnValue;

        if (collapse) {
          returnValue = (
            <SidenavItem
              key={key ?? i}
              color={color}
              name={name}
              active={key === itemParentName ? "isParent" : false}
              data-testid="collapse"
              className={tourClass(key)}
              open={openNestedCollapse === key}
              onClick={({ currentTarget }: any) =>
                openNestedCollapse === key && currentTarget.classList.contains("MuiListItem-root")
                  ? setOpenNestedCollapse(false)
                  : setOpenNestedCollapse(key)
              }
            >
              {renderNestedCollapse(collapse, user)}
            </SidenavItem>
          );
        } else {
          returnValue = href ? (
            <Link
              href={href}
              key={key ?? i}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavItem
                color={color}
                name={name}
                active={route === pathname || pathname?.includes(route)}
                data-testid="href"
                className={tourClass(key)}
              />
            </Link>
          ) : (
            <NavLink to={route} key={key} style={{ textDecoration: "none" }}>
              <SidenavItem
                color={color}
                name={name}
                active={route === pathname || pathname?.includes(route)}
                data-testid="link"
                className={tourClass(key)}
              />
            </NavLink>
          );
        }
        return <SidenavList key={key ?? i}>{returnValue}</SidenavList>;
      });

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes
    .filter((x) =>
      x.permission ? signedInUser && checkPermissions(signedInUser, x.permission as any) : true
    )
    .map(({ type, name, icon, title, collapse, noCollapse, key, href, route, target }: any, i) => {
      let returnValue;

      if (type === "internal") {
        return null;
      } else if (type === "link") {
        if (route || href) {
          returnValue = (
            <SidenavList key={key ?? i}>
              {href ? (
                <Link
                  href={href}
                  target={target}
                  rel={target === "_blank" ? "noreferrer" : undefined}
                  key={key}
                  style={{ textDecoration: "none" }}
                >
                  <SidenavLink
                    icon={icon}
                    color={color}
                    name={name}
                    active={key === itemName}
                    data-testid="inner-href"
                    className={tourClass(key)}
                  />
                </Link>
              ) : (
                <NavLink to={route} key={key} style={{ textDecoration: "none" }}>
                  <SidenavLink
                    icon={icon}
                    color={color}
                    name={name}
                    active={route === pathname ?? pathname?.includes(route)}
                    data-testid="inner-link"
                    className={tourClass(key)}
                  />
                </NavLink>
              )}
            </SidenavList>
          );
        }
      } else if (type === "collapse") {
        if (href) {
          returnValue = (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavCollapse
                name={name}
                icon={icon}
                active={key === collapseName}
                noCollapse={noCollapse}
                data-testid="sidenav-collapse-href"
                className={tourClass(key)}
              />
            </Link>
          );
        } else if (noCollapse && route) {
          returnValue = (
            <NavLink to={route} key={key}>
              <SidenavCollapse
                name={name}
                icon={icon}
                noCollapse={noCollapse}
                active={key === collapseName}
                data-testid="sidenav-collapse-not-href"
                className={tourClass(key)}
              >
                {collapse ? renderCollapse(collapse, signedInUser) : null}
              </SidenavCollapse>
            </NavLink>
          );
        } else {
          const routes =
            collapse?.map((collapseRoute) => collapseRoute.route).filter(Boolean) ?? [];

          const active =
            routes.includes(pathname) || routes.find((route) => pathname.includes(route));

          returnValue =
            name === "User" ? (
              <SidenavCollapse
                key={key}
                name={getFullName(signedInUser ?? ({ firstName: "", lastName: "" } as any))}
                icon={
                  signedInUser && signedInUser.avatar ? (
                    <img
                      width="24px"
                      height="24px"
                      src={getImageAssetURL(signedInUser.avatar)}
                      style={{ borderRadius: "4px" }}
                    />
                  ) : undefined
                }
                active={active}
                data-testid="user-link"
                className={tourClass(key)}
                open={openCollapse === key}
                onClick={() =>
                  openCollapse === key ? setOpenCollapse(false) : setOpenCollapse(key)
                }
              >
                {collapse ? renderCollapse(collapse, signedInUser) : null}
              </SidenavCollapse>
            ) : (
              <SidenavCollapse
                key={key}
                name={name}
                icon={icon}
                active={active}
                data-testid="not-user-link"
                className={tourClass(key)}
                open={openCollapse === key}
                onClick={() =>
                  openCollapse === key ? setOpenCollapse(false) : setOpenCollapse(key)
                }
              >
                {collapse ? renderCollapse(collapse, signedInUser) : null}
              </SidenavCollapse>
            );
        }
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key ?? i}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }

      return returnValue;
    });

  return (
    <SidenavRoot
      {...rest}
      className="no-print"
      variant={mobileNavbar ? null : "permanent"}
      open={mobileNavbar ? (miniSidenav ? false : true) : null}
      onClose={mobileNavbar ? closeSidenav : null}
      mobileNavbar={mobileNavbar}
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && (
            <MDBox
              component="img"
              src={brand}
              alt="Brand"
              width="2rem"
              display={miniSidenav ? "block" : "none"}
            />
          )}
          <MDBox width={"100%"} sx={(theme: any) => sidenavLogoLabel(theme, { miniSidenav })}>
            <MDBox component="img" src={brandLogo} alt="Brand" width="11rem" />
          </MDBox>
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderRoutes}</List>
      {onHelpClick && (
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent={miniSidenav ? "center" : "flex-start"}
          px={miniSidenav ? 0 : 4}
          py={1.5}
          mt="auto"
        >
          <TourHelpButton onClick={onHelpClick} color="light" />
          {!miniSidenav && (
            <MDTypography
              variant="button"
              fontWeight="regular"
              color="text"
              sx={{ cursor: "pointer", ml: 1 }}
              onClick={onHelpClick}
            >
              Help
            </MDTypography>
          )}
        </MDBox>
      )}
    </SidenavRoot>
  );
}

// Declaring default props for Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

export default Sidenav;
